/**
 * Status Builder wire mapping.
 *
 * The C# pipeline reads the Status Builder section from the AdvancedSettings JSON
 * with DEFAULT System.Text.Json options — case-SENSITIVE PascalCase property names;
 * enum VALUES parse case-insensitively. This module converts between the UI store
 * shape (camelCase + row ids) and that wire shape, in both directions.
 *
 * C# contract (PipeEX.Models.DTO.Pcf.StatusBuilderSettings):
 *   { "StatusBuilder": { "Categories": [ { "Name", "PhaseKind", "PhaseNames",
 *     "Attributes": [ { "PcfAttribute", "Source", "Value" } ] } ] } }
 */

import type {
  StatusAttributeRow,
  StatusBuilderConfiguration,
  StatusCategory,
  StatusPhaseKind,
  StatusValueSource,
} from '../../types/config/status-builder.types';

export interface WireStatusAttributeRow {
  PcfAttribute: string;
  Source: StatusValueSource;
  Value: string;
}

export interface WireStatusCategory {
  Name: string;
  PhaseKind: StatusPhaseKind;
  PhaseNames: string[];
  Attributes: WireStatusAttributeRow[];
}

export interface WireStatusBuilder {
  Categories: WireStatusCategory[];
}

const isValidRow = (row: StatusAttributeRow): boolean =>
  row.pcfAttribute.trim().length > 0 && row.value.trim().length > 0;

/**
 * Builds the PascalCase wire section from the store configuration. Rows without an
 * attribute name or value are dropped (the pipeline would skip them anyway).
 * Returns undefined when NO category carries a valid row — the key is then omitted
 * from the payload entirely, which the pipeline treats as "feature off".
 */
export function buildStatusBuilderWireSection(
  config: StatusBuilderConfiguration
): WireStatusBuilder | undefined {
  const categories: WireStatusCategory[] = (config?.categories ?? [])
    .map((category) => ({
      Name: category.name,
      PhaseKind: category.phaseKind ?? 'Created',
      PhaseNames: (category.phaseNames ?? []).map((n) => n.trim()).filter((n) => n.length > 0),
      Attributes: (category.attributes ?? []).filter(isValidRow).map((row) => ({
        PcfAttribute: row.pcfAttribute.trim(),
        Source: row.source,
        Value: row.value.trim(),
      })),
    }))
    .filter((category) => category.Attributes.length > 0);

  return categories.length > 0 ? { Categories: categories } : undefined;
}

const normalizePhaseKind = (raw: unknown): StatusPhaseKind =>
  typeof raw === 'string' && raw.toLowerCase() === 'demolished' ? 'Demolished' : 'Created';

const normalizeSource = (raw: unknown): StatusValueSource =>
  typeof raw === 'string' && raw.toLowerCase() === 'revitparameter' ? 'RevitParameter' : 'Constant';

const slugify = (name: string, index: number): string => {
  const slug = name.trim().toLowerCase().replace(/[^a-z0-9а-яё]+/gi, '-').replace(/^-+|-+$/g, '');
  return slug.length > 0 ? slug : `category-${index}`;
};

/**
 * Parses the wire section (as found inside the parsed AdvancedSettings base object)
 * back into the store configuration shape, generating stable-ish ids for React keys.
 * Tolerates missing/foreign-cased enum values. Returns null when the section is
 * absent or carries no categories, so the caller can keep the current store state.
 */
export function parseStatusBuilderWireSection(
  advancedBase: unknown
): StatusBuilderConfiguration | null {
  const section = (advancedBase as { StatusBuilder?: { Categories?: unknown } } | null)?.StatusBuilder;
  const rawCategories = section?.Categories;
  if (!Array.isArray(rawCategories) || rawCategories.length === 0) {
    return null;
  }

  const categories: StatusCategory[] = rawCategories.map((raw: any, index: number) => {
    const name = typeof raw?.Name === 'string' ? raw.Name : `Category ${index + 1}`;
    const id = slugify(name, index);
    const attributes: StatusAttributeRow[] = Array.isArray(raw?.Attributes)
      ? raw.Attributes.map((row: any, rowIndex: number) => ({
          id: `${id}-attr-${rowIndex}`,
          pcfAttribute: typeof row?.PcfAttribute === 'string' ? row.PcfAttribute : '',
          source: normalizeSource(row?.Source),
          value: typeof row?.Value === 'string' ? row.Value : '',
        }))
      : [];

    return {
      id,
      name,
      color: id === 'existing' ? 'success' : id === 'demolition' ? 'error' : 'info',
      isBuiltIn: id === 'existing' || id === 'demolition',
      phaseKind: normalizePhaseKind(raw?.PhaseKind),
      phaseNames: Array.isArray(raw?.PhaseNames)
        ? raw.PhaseNames.filter((n: unknown): n is string => typeof n === 'string')
        : [],
      attributes,
    };
  });

  return { categories };
}
